import { createAsyncThunk } from '@reduxjs/toolkit';

import ActionClient from 'api/actionClient';
import dayjs from 'dayjs';
import { notifySuccess } from 'utils/notifyConfig';

// Actions
export const getActions = createAsyncThunk(
  'actions/get',
  async (params, { rejectWithValue }) => {
    try {
      const response = await ActionClient.get('/settings/actions', { params });
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get actions for dropdown (separate from paginated table data)
export const getDropdownActions = createAsyncThunk(
  'actions/getDropdown',
  async (params, { rejectWithValue }) => {
    try {
      const response = await ActionClient.get('/settings/actions', { params });
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createAction = createAsyncThunk(
  'actions/create',
  async (actionData, { rejectWithValue }) => {
    try {
      const response = await ActionClient.post('/settings/actions', actionData);
      notifySuccess('Action created successfully');
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Action name already exists');
    }
  }
);

export const getActionById = createAsyncThunk(
  'actions/getById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ActionClient.get(`${'/settings/actions'}/${id}`);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateAction = createAsyncThunk(
  'actions/update',
  async ({ id, actionData }, { rejectWithValue }) => {
    try {
      const response = await ActionClient.put(`${'/settings/actions'}/${id}`, actionData);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const enableAction = createAsyncThunk(
  'actions/enable',
  async (id, { rejectWithValue }) => {
    try {
      await ActionClient.patch(`${'/settings/actions'}/${id}/enable`, {});
      notifySuccess('Action enabled successfully');
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const disableAction = createAsyncThunk(
  'actions/disable',
  async ({ id, effectiveDate }, { rejectWithValue }) => {
    try {
      const response = await ActionClient.patch(`${'/settings/actions'}/${id}/disable`, {
        effectiveDate,
      });
      if (effectiveDate) {
        const formattedDate = dayjs(effectiveDate).format('DD-MM-YYYY');
        notifySuccess(`Action is scheduled to be disabled on ${formattedDate}.`);
      } else {
        notifySuccess('Action disabled successfully.');
      }
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Time Settings
export const getTimeSettings = createAsyncThunk(
  'settings/getTime',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ActionClient.get('/settings/time');
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateTimeSettings = createAsyncThunk(
  'settings/updateTime',
  async (timeData, { rejectWithValue }) => {
    try {
      const response = await ActionClient.post('/settings/time', timeData);
      notifySuccess('Time settings updated successfully');
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getTimeSettingById = createAsyncThunk(
  'settings/getTimeById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ActionClient.get(`${'/settings/time'}/${id}`);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const putTimeSetting = createAsyncThunk(
  'settings/putTime',
  async ({ id, timeData }, { rejectWithValue }) => {
    try {
      const response = await ActionClient.put(`${'/settings/time'}/${id}`, timeData);
      notifySuccess('Settings saved successfully');
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Assignments
export const getAssignments = createAsyncThunk(
  'actions/getAssignments',
  async (params, { rejectWithValue }) => {
    try {
      const response = await ActionClient.get('/settings/assignments', { params });
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createAssignment = createAsyncThunk(
  'actions/createAssignment',
  async ({ assignmentData, scope }, { rejectWithValue }) => {
    try {
      const response = await ActionClient.post(
        `${'/settings/assignments'}/${scope}`,
        assignmentData
      );
      notifySuccess('Assignment created successfully');
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create assignment');
    }
  }
);

export const removeAssignment = createAsyncThunk(
  'actions/removeAssignment',
  async ({ id, effectiveDate }, { rejectWithValue }) => {
    try {
      await ActionClient.patch(`${'/settings/assignments'}/${id}/remove`, { effectiveDate });
      notifySuccess('Assignment removal scheduled successfully');
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateAssignment = createAsyncThunk(
  'actions/updateAssignment',
  async ({ id, scope, assignmentData }, { rejectWithValue }) => {
    try {
      const response = await ActionClient.put(
        `/settings/assignments/${scope}/${id}`,
        assignmentData
      );

      notifySuccess('Assignment updated successfully');

      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getAssignmentById = createAsyncThunk(
  'actions/getAssignmentById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ActionClient.get(`${'/settings/assignments'}/${id}`);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getEmployees = createAsyncThunk(
  'actions/getEmployees',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await ActionClient.get('/employees', { params });
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getDepartments = createAsyncThunk(
  'actions/getDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ActionClient.get('/departments');
      return response || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getDashboardData = createAsyncThunk(
  'actions/getDashboardData',
  async (params, { rejectWithValue }) => {
    try {
      const response = await ActionClient.get('/dashboard', { params });
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getRecordHistory = createAsyncThunk(
  'actions/getRecordHistory',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ActionClient.get(`/dashboard/records/${id}/history`);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getDepartmentKPI = createAsyncThunk(
  'actions/getDepartmentKPI',
  async (params, { rejectWithValue }) => {
    try {
      const response = await ActionClient.get('/dashboard/department-kpi', { params });
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const submitRecord = createAsyncThunk(
  'actions/submitRecord',
  async ({ id, submitData }, { rejectWithValue }) => {
    console.log('Submitting record:', { id, submitData });
    try {
      const response = await ActionClient.put(`/dashboard/records/${id}/submit`, submitData);
      notifySuccess('Record submitted successfully');
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const reviewRecord = createAsyncThunk(
  'actions/reviewRecord',
  async ({ id, reviewData }, { rejectWithValue }) => {
    try {
      const response = await ActionClient.put(`/dashboard/records/${id}/review`, reviewData);
      notifySuccess('Record reviewed and modified');
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const approveRecord = createAsyncThunk(
  'actions/approveRecord',
  async (id, { rejectWithValue }) => {
    try {
      await ActionClient.put(`/dashboard/records/${id}/approve`);
      notifySuccess('Record approved successfully');
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const actualRecord = createAsyncThunk(
  'actions/actualRecord',
  async ({ id, actualData }, { rejectWithValue }) => {
    console.log('Submitting actual values:', { id, actualData });

    try {
      const response = await ActionClient.put(`/dashboard/records/${id}/actual`, actualData);
      notifySuccess('Actual values saved successfully');
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);
